import type { RouteComponent, RouteRecordRaw, RouteRecordRedirectOption } from 'vue-router';
import { type RouteRecord } from '@crm-lib/types';
import { type StyledFontAwesomeIcons } from '@shared/plugin/icons/types';
import { instellingenIcons } from '@crm-lib/instellingen/icons';

export type InstellingenMenuGroup = 'Algemeen' | 'Offertes/Aanvragen' | 'Risicoscan';

type InstellingenVisibleOptions = {
  heeftInstellingenPermissie: boolean;
  heeftWebmodulePermissie: boolean;
  instellingenOverigMetDkimInfoInVueToggle: boolean;
};

export type InstellingenRouteRecord = RouteRecordRaw & {
  meta: {
    title: string;
    label: string;
    icon: StyledFontAwesomeIcons;
    pagina?: string;
    group: InstellingenMenuGroup;
    default?: boolean;
  };
  isVisible?: (options: InstellingenVisibleOptions) => boolean;
};

const fullPageTitle = (tabName: string) => `Instellingen - ${tabName}`;

const createRoute = (
  tabName: string,
  routeName: string,
  icon: StyledFontAwesomeIcons,
  pagina: string | undefined,
  options?: {
    pageTitle?: string;
    children?: RouteRecord[];
    redirect?: RouteRecordRedirectOption;
    group?: InstellingenMenuGroup;
    component?: RouteComponent;
    isVisible?: (options: InstellingenVisibleOptions) => boolean;
  },
): InstellingenRouteRecord => ({
  path: routeName,
  redirect: options?.redirect,
  name: `instellingen-${routeName}`,
  isVisible: options?.isVisible,
  meta: {
    title: fullPageTitle(options?.pageTitle || tabName),
    label: options?.pageTitle || tabName,
    icon,
    pagina,
    group: options?.group ?? 'Algemeen',
  },
  children: options?.children ?? [],
  component: options?.component ?? (() => import('@crm-lib/instellingen/InstellingenEmbed.vue')),
});

const heeftInstellingenPermissie = (options: InstellingenVisibleOptions) =>
  options.heeftInstellingenPermissie;

const routes: InstellingenRouteRecord[] = [
  // Algemeen
  createRoute(
    'Medewerkers',
    'medewerkers',
    instellingenIcons.medewerkers,
    '/content/beheer/tussenpersoon/',
    {
      group: 'Algemeen',
      isVisible: heeftInstellingenPermissie,
    },
  ),
  createRoute(
    'Jouw account',
    'jouw-account',
    instellingenIcons.jouwAccount,
    '/content/beheer/tussenpersoon/',
    {
      group: 'Algemeen',
      isVisible: (options) => !options.heeftInstellingenPermissie,
    },
  ),

  // Nieuwe email-authenticatie en SEPA pagina's
  createRoute(
    'E-mail authenticatie',
    'emailAuthenticatie',
    instellingenIcons.emailAuthenticatie,
    undefined,
    {
      group: 'Algemeen',
      component: () => import('../instellingen/EMailAuthenticatie/EMailAuthenticatie.vue'),
      isVisible: (options) =>
        options.heeftInstellingenPermissie && !!options.instellingenOverigMetDkimInfoInVueToggle,
    },
  ),

  // Oude email-authenticatie en SEPA pagina's
  createRoute(
    'E-mail authenticatie',
    'asp-email-authenticatie',
    instellingenIcons.emailAuthenticatie,
    '/content/beheer/tussenpersoon/algemeen/mailauthenticatie/',
    {
      group: 'Algemeen',
      isVisible: (options) =>
        options.heeftInstellingenPermissie && !options.instellingenOverigMetDkimInfoInVueToggle,
    },
  ),
  createRoute(
    'SEPA machtiging',
    'asp-sepa-machtiging',
    instellingenIcons.sepa,
    '/content/beheer/tussenpersoon/algemeen/SEPAMachtiging',
    {
      group: 'Algemeen',
      isVisible: (options) =>
        options.heeftInstellingenPermissie && !options.instellingenOverigMetDkimInfoInVueToggle,
    },
  ),

  createRoute(
    'Kantoor logo',
    'kantoor-logo',
    instellingenIcons.kantoorLogo,
    '/content/beheer/tussenpersoon/correspondentie/logo/',
    {
      group: 'Algemeen',
      isVisible: heeftInstellingenPermissie,
    },
  ),
  createRoute(
    'Pakketlabels',
    'pakketlabels',
    instellingenIcons.pakketlabels,
    '/content/beheer/tussenpersoon/correspondentie/labels/',
    {
      group: 'Algemeen',
      isVisible: heeftInstellingenPermissie,
    },
  ),
  createRoute('Alerts', 'alerts', instellingenIcons.alerts, '/net/alert/AlertAdminTP/', {
    group: 'Algemeen',
    isVisible: heeftInstellingenPermissie,
  }),
  createRoute(
    'Standaard alertteksten',
    'standaard-alertteksten',
    instellingenIcons.standaardAlertTeksten,
    undefined,
    {
      group: 'Algemeen',
      component: () => import('../standaardalerts/StandaardAlerts.page.vue'),
      isVisible: heeftInstellingenPermissie,
    },
  ),

  // Offertes/Aanvragen
  createRoute(
    'Provisie per product',
    'provisie-per-product',
    instellingenIcons.provisie,
    '/content/beheer/tussenpersoon/boaz/provisie/',
    {
      group: 'Offertes/Aanvragen',
      isVisible: heeftInstellingenPermissie,
    },
  ),
  createRoute(
    'Standaard mailteksten',
    'standaard-mailteksten',
    instellingenIcons.mailteksten,
    '/content/beheer/tussenpersoon/boaz/mailteksten/',
    {
      group: 'Offertes/Aanvragen',
    },
  ),
  createRoute(
    'Verzend voorkeur',
    'verzend-voorkeur',
    instellingenIcons.verzendVoorkeur,
    '/content/beheer/tussenpersoon/boaz/mailadressen/',
    {
      group: 'Offertes/Aanvragen',
      isVisible: heeftInstellingenPermissie,
    },
  ),
  createRoute(
    'Webmodules',
    'webmodules',
    instellingenIcons.webmodules,
    '/content/beheer/tussenpersoon/boaz/webmodules/',
    {
      group: 'Offertes/Aanvragen',
      isVisible: (options) => options.heeftInstellingenPermissie && options.heeftWebmodulePermissie,
    },
  ),
  createRoute(
    'Offerte defaults',
    'offerte-defaults',
    instellingenIcons.offerteDefaults,
    '/content/beheer/tussenpersoon/boaz/overig/',
    {
      group: 'Offertes/Aanvragen',
    },
  ),

  // Risicoscan
  createRoute(
    'Algemeen',
    'risicoscan-algemeen',
    instellingenIcons.risicoscanAlgemeen,
    '/net/riskinventarisation/RITAdminTPGeneral/',
    {
      group: 'Risicoscan',
      isVisible: heeftInstellingenPermissie,
    },
  ),
  createRoute(
    'Vragen',
    'risicoscan-vragen',
    instellingenIcons.risicoscanVragen,
    '/net/riskinventarisation/RITAdminTPQuestions/',
    {
      group: 'Risicoscan',
      isVisible: heeftInstellingenPermissie,
    },
  ),
];

export default routes;
